import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { InputText } from 'primereact/inputtext'
import { FaUser, FaListAlt } from 'react-icons/fa'

import ReusableModal from '../../shared/ReuseableModal'
import PatientListModal from '../his/visits/PatientListModal'
import { fetchData } from '../../helpers/fetchData'
import {
  setIsGeneralSearch,
  setSelectPatientData
} from '../../redux/slices/BeneficiarySlice'

function PatientSearchFeild () {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [openListModal, setOpenListModal] = useState(false)
  const [listModalData, setListModalData] = useState([])
  const [filterText, setFilterText] = useState('')
  const [selected, setSelected] = useState('file_no')
  const [contactPagination, setContactPagination] = useState({
    page: 1,
    perPage: 10
  })

  const [openChoiceModal, setOpenChoiceModal] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [patientVisits, setPatientVisits] = useState([])

  const searchOptions = [
    {
      label: t('MRN'),
      value: 'file_no',
      key: 'file_no',
      errorMsg: 'Enter a file number to search.'
    },
    {
      label: t('Document Id'),
      value: 'doc_id',
      key: 'document_id',
      errorMsg: 'Enter Document Id to search.'
    },
    {
      label: t('Mobile No'),
      value: 'mob_no',
      key: 'contact',
      errorMsg: 'Enter Mobile No to search.'
    }
  ]

  const handleSearch = async () => {
    const option = searchOptions.find(opt => opt.value === selected)
    if (!filterText.trim()) {
      toast.error(option.errorMsg)
      return
    }

    setLoading(true)
    try {
      const res = await fetchData('POST', '/v3/patient/filter', {
        [option.key]: filterText.trim(),
        ...(option.value === 'mob_no' ? contactPagination : {})
      })

      const patients = res?.data || []
      if (patients.length < 1) {
        toast.error('No Patient Found')
        return
      }

      if (option.value === 'mob_no' && patients.length > 1) {
        setListModalData(res)
        setOpenListModal(true)
      } else {
        const patientData = patients[0]
        checkPatientVisits(patientData)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const isActive = val => val === selected

  const checkPatientVisits = async patientData => {
    setLoading(true)
    try {
      const res = await fetchData('POST', '/v3/visit/getPatientVisitByIdV3', {
        patient_id: patientData.patient_id,
        status: 'All'
      })

      if (res?.visits?.length > 0) {
        setPatientVisits(res.visits)
        setSelectedPatient(patientData)
        setOpenChoiceModal(true)
      } else {
        dispatch(setIsGeneralSearch(true))
        dispatch(setSelectPatientData(patientData))
        navigate('/patient-profile/emr')
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='w-full hidden md:flex items-center justify-end'>
      <InputText
        style={{
          borderRadius: '0px',
          border: 'none',
          backgroundColor: 'white'
        }}
        disabled={loading}
        value={filterText}
        onChange={e => setFilterText(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleSearch()}
        placeholder={`${t('Search with')} ${t(
          selected === 'file_no'
            ? 'MRN'
            : selected === 'doc_id'
            ? 'Document ID'
            : 'Mobile No'
        )}`}
        className={`h-[30px] ${
          i18n.language == 'en' ? '!rounded-l-md' : '!rounded-r-md'
        } border-r-0 focus:ring-0 !bg-gray-200 focus:border-blue-500 w-full max-w-[250px]`}
      />

      <div className='flex items-center shrink-0'>
        {searchOptions.map((option, index) => (
          <button
            key={option.value}
            className={`px-2 py-2 text-sm font-medium h-[30px] min-w-[80px] transition-all duration-200 ${
              i18n.language == 'en' ? 'border-l' : 'border-r'
            } ${
              index === 2
                ? i18n.language == 'en'
                  ? 'rounded-r-md'
                  : 'rounded-l-md'
                : null
            } border-gray-300 ${
              isActive(option.value)
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            onClick={() => setSelected(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <ReusableModal
        isVisible={openListModal}
        title={t('Patient List')}
        HeaderIcon={FaUser}
        onClose={() => {
          setOpenListModal(false)
          setContactPagination({ page: 1, perPage: 10 })
          setListModalData([])
        }}
        position='top'
      >
        <PatientListModal
          data={listModalData}
          isSearch={true}
          payload={contactPagination}
          setPayloadData={setContactPagination}
          handlePatientSelect={patientData => {
            checkPatientVisits(patientData)
            setOpenListModal(false)
          }}
          setListModalData={setListModalData}
          setOpenListModal={setOpenListModal}
        />
      </ReusableModal>

      <ReusableModal
        isVisible={openChoiceModal}
        title={selectedPatient?.full_name || t('Select Action')}
        HeaderIcon={FaUser}
        onClose={() => setOpenChoiceModal(false)}
        position='top'
        className='w-[95%] md:w-[450px]'
      >
        <div className='p-6'>
          <p className='text-sm text-center text-gray-500 mb-6'>
            {t('Please select the area you wish to access')}
          </p>

          <div className='grid grid-cols-2 gap-4'>
            <div
              onClick={() => {
                dispatch(setIsGeneralSearch(true))
                dispatch(setSelectPatientData(selectedPatient))
                navigate('/patient-profile/emr')
                setOpenChoiceModal(false)
              }}
              className='flex flex-col items-center justify-center p-6 bg-white border border-gray-200 rounded-xl cursor-pointer transition-all hover:border-primary hover:bg-gray-50 group'
            >
              <FaUser
                size={28}
                className='text-gray-400 group-hover:text-primary transition-colors mb-3'
              />
              <span className='text-[13px] font-bold text-gray-700 group-hover:text-primary uppercase tracking-tight'>
                {t('Patient Profile')}
              </span>
            </div>

            <div
              onClick={() => {
                navigate('/patient-active-orders', {
                  state: {
                    patientData: selectedPatient
                  }
                })
                setOpenChoiceModal(false)
              }}
              className='relative flex flex-col items-center justify-center p-6 bg-white border border-gray-200 rounded-xl cursor-pointer transition-all hover:border-green-500 hover:bg-gray-50 group'
            >
              <FaListAlt
                size={28}
                className='text-gray-400 group-hover:text-green-600 transition-colors mb-3'
              />
              <span className='text-[13px] font-bold text-gray-700 group-hover:text-green-600 uppercase tracking-tight'>
                {t('Patient Orders')}
              </span>

              {patientVisits.length > 0 && (
                <span className='absolute top-3 right-3 bg-red-500 text-white text-[10px] font-bold h-5 w-5 flex items-center justify-center rounded-full ring-2 ring-white'>
                  {patientVisits.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </ReusableModal>

      {/* Visit list now opens on /patient-active-orders. */}
    </div>
  )
}

export default PatientSearchFeild
